// src/components/rcv/asociaciones/EntidadDialog.jsx
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel,
  Select, MenuItem, Alert, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { entidadesService } from '../../../services/entidadesService';

const EntidadDialog = ({ open, onClose, onEntidadCreada }) => {
  // Estado para nueva entidad
  const [nuevaEntidad, setNuevaEntidad] = useState({
    rut: '',
    dv: '',
    nombre: '',
    tipo: 'EMPRESA',
    activa: 1
  });
  
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  
  // Manejar cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'rut') {
      // Solo permitir números para el RUT
      const cleanedValue = value.replace(/\D/g, '');
      setNuevaEntidad({ ...nuevaEntidad, [name]: cleanedValue });
    } else if (name === 'dv') {
      // Permitir números y K para el dígito verificador
      const cleanedValue = value.replace(/[^0-9kK]/g, '').toUpperCase();
      // Solo tomar el primer carácter
      setNuevaEntidad({ ...nuevaEntidad, [name]: cleanedValue.charAt(0) || '' });
    } else {
      setNuevaEntidad({ ...nuevaEntidad, [name]: value });
    }
  };
  
  // Guardar nueva entidad
  const handleGuardar = async () => {
    // Validar campos obligatorios
    if (!nuevaEntidad.rut || !nuevaEntidad.dv || !nuevaEntidad.nombre) {
      setError('Todos los campos marcados con * son obligatorios');
      return;
    }
    
    setGuardando(true);
    setError('');
    
    try {
      // Crear la nueva entidad
      const entidadCreada = await entidadesService.crearEntidad(nuevaEntidad);
      
      // Notificar al componente padre
      if (onEntidadCreada) {
        onEntidadCreada(entidadCreada);
      }
      
      // Cerrar diálogo y reiniciar estado
      handleClose();
    } catch (err) {
      setError(err.message || 'Error al crear entidad');
    } finally {
      setGuardando(false);
    }
  };
  
  // Cerrar diálogo y reiniciar estado
  const handleClose = () => {
    setNuevaEntidad({
      rut: '',
      dv: '',
      nombre: '',
      tipo: 'EMPRESA',
      activa: 1
    });
    setError('');
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Crear Nueva Entidad</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={8}>
            <TextField
              label="RUT (sin dígito verificador)"
              fullWidth
              required
              name="rut"
              value={nuevaEntidad.rut}
              onChange={handleChange}
              placeholder="Ej: 77123456"
              inputProps={{ maxLength: 9 }}
              disabled={guardando}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="DV"
              fullWidth
              required
              name="dv"
              value={nuevaEntidad.dv}
              onChange={handleChange}
              placeholder="Ej: K"
              inputProps={{ maxLength: 1 }}
              disabled={guardando}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Nombre de la Entidad"
              fullWidth
              required
              name="nombre"
              value={nuevaEntidad.nombre}
              onChange={handleChange}
              placeholder="Nombre o razón social"
              disabled={guardando}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth disabled={guardando}>
              <InputLabel>Tipo de Entidad</InputLabel>
              <Select
                name="tipo"
                value={nuevaEntidad.tipo}
                label="Tipo de Entidad"
                onChange={handleChange}
              >
                <MenuItem value="EMPRESA">Empresa</MenuItem>
                <MenuItem value="PERSONA">Persona Natural</MenuItem>
                <MenuItem value="ORGANIZACION">Organización</MenuItem>
                <MenuItem value="GUBERNAMENTAL">Entidad Gubernamental</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button 
          onClick={handleGuardar} 
          variant="contained" 
          color="primary"
          disabled={guardando}
          startIcon={guardando ? <CircularProgress size={20} /> : <AddIcon />}
        >
          {guardando ? 'Guardando...' : 'Crear Entidad'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntidadDialog;